import { motion } from 'framer-motion';
import { 
  Receipt, ArrowRight, CheckCircle, XCircle,
  Bell, AlertTriangle, TrendingUp, CreditCard
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const problems = [
  "Des factures impayees qui s'accumulent",
  "Des paiements en retard non suivis",
  "Des relances oubliees faute de temps",
  "Une tresorerie tendue qui freine ton activite",
  "Pas le temps d'encaisser et de relancer",
];

const features = [
  { icon: Receipt, title: "Suivi des factures", desc: "Visualisez toutes tes factures en un coup d'oeil" },
  { icon: CreditCard, title: "Paiements recus", desc: "Voyez instantanement les paiements encaisses" },
  { icon: AlertTriangle, title: "Factures en attente", desc: "Identifiez les factures qui attendent d'etre payees" },
  { icon: Bell, title: "Alertes automatiques", desc: "Recevez des notifications pour les factures en retard" },
];

const benefits = [
  "Moins de factures oubliees",
  "Tresorerie saine et previsible",
  "Suivi simple et visuel",
  "Relances automatisees sans effort",
  "Plus d'argent qui dort chez tes clients",
];

export default function SuiviPaiementsPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Suivi Paiements | Gestion Trésorerie & Relances - BatiAssist"
        description="Suis tes factures et paiements en temps réel : alertes automatiques, relances clients, visualisation trésorerie. Fini les factures impayées oubliées."
        keywords="suivi paiement artisan, relance facture automatique, trésorerie BTP, gestion impayés, facturation bâtiment"
        path="/fonctionnalites/suivi-paiements"
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-600 text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
              <Receipt className="w-4 h-4" />
              Suivi des paiements
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Ne perdez plus d'argent{' '}
              <span className="text-ba-secondary">avec les factures oubliees</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Suivez tous tes paiements en temps reel. Soyez alerte des que 
              une facture est en retard. Ne laissez plus d'argent sur la table.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                Suivre mes paiements
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-3 py-1 bg-red-100 text-red-600 text-xs font-semibold uppercase rounded-full mb-4">
                Le probleme
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Les artisans perdent de l'argent chaque mois
              </h2>
              <p className="text-lg text-slate-600">
                Entre les chantiers et l'administratif, il est facile d'oublier de relancer un client 
                ou de perdre la trace d'une facture. Resultat : de l'argent qui dort.
              </p>
            </motion.div>
            <div className="space-y-4">
              {problems.map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-red-50 rounded-xl border border-red-100"
                >
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <span className="text-slate-700">{problem}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Tout tes paiements sous controle
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              BatiAssist te donne une vue complete de ta trésorerie
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl border border-slate-200 text-center"
                >
                  <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-600">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Demo */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Alertes et relances automatiques
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Le systeme te prévient automatiquement quand une facture arrive a echeance 
                ou quand un client est en retard de paiement.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <Bell className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-800">Facture #2024-0038 arrive a echeance</p>
                    <p className="text-sm text-slate-500">Client: Famille Dupont - 3 500€</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-red-50 rounded-xl border border-red-200">
                  <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-800">Retard de paiement - 15 jours</p>
                    <p className="text-sm text-slate-500">Client: Entreprise Martin - 8 200€</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-3xl p-8"
            >
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <h3 className="font-bold text-slate-800 mb-6">Tableau de bord paiements</h3>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600 mb-1">42 500€</p>
                    <p className="text-xs text-slate-500">Encaisse ce mois</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600 mb-1">12 300€</p>
                    <p className="text-xs text-slate-500">En attente</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { client: 'Villa Garcia', amount: '5 200€', status: 'Paye', color: 'bg-emerald-100 text-emerald-600' },
                    { client: 'Maison Leroy', amount: '3 800€', status: 'En attente', color: 'bg-amber-100 text-amber-600' },
                    { client: 'Appt. Rossi', amount: '2 100€', status: 'En retard', color: 'bg-red-100 text-red-600' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium text-slate-700">{item.client}</span>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{item.amount}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${item.color}`}>{item.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-8">
                Les benefices pour ta trésorerie
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <CheckCircle className="w-6 h-6 text-ba-success flex-shrink-0" />
                    <span className="text-lg text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 border border-slate-200 shadow-lg"
            >
              <TrendingUp className="w-12 h-12 text-emerald-500 mb-4" />
              <h3 className="text-2xl font-bold text-slate-800 mb-4">Ameliorez ta trésorerie</h3>
              <p className="text-slate-600 leading-relaxed">
                En suivant tes paiements de pres et en relancant rapidement les clients en retard, 
                tu améliores significativement ta trésorerie. Plus d'argent qui dort, 
                plus de factures oubliees.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-emerald-600">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Arretez de perdre de l'argent
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Suivez tes paiements et améliore ta tresorerie des maintenant.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-white hover:bg-white/90 text-emerald-600 font-bold rounded-full px-10 py-7 text-lg shadow-xl">
                Suivre mes paiements
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
